/**
 * repo.messages.ts —— 消息仓储
 *
 * 消息是只追加的（append-only）：发出去就不再改内容，只改状态（sending → sent → read）。
 * 这个性质简化了很多事 —— 分页只需要按时间游标往前翻，不用担心插入乱序。
 */

import {
  STORE,
  dbGet,
  dbPut,
  dbDelete,
  dbQueryByIndex,
  dbQueryByIndexDesc,
  uid,
} from './db';
import { eventBus } from '@/core/event-bus';
import { sessionsRepo } from './repo.sessions';
import type { Message, MessageType, MessageStatus } from '@/types/models';

export interface SendMessageInput {
  sessionId: string;
  /** 'me' 或 contactId */
  sender: string;
  text: string;
  type?: MessageType;
  fileId?: string;
  /** 指定消息时间。仅用于构造演示数据等需要时间分布的场景，正常发送不传 */
  createdAt?: number;
}

export const messagesRepo = {
  async get(id: string): Promise<Message | undefined> {
    return dbGet<Message>(STORE.messages, id);
  },

  /** 某会话的消息，按时间正序（旧的在前，符合聊天界面阅读顺序） */
  async listBySession(sessionId: string): Promise<Message[]> {
    const all = await dbQueryByIndex<Message>(STORE.messages, 'sessionId', sessionId);
    return all.sort((a, b) => a.createdAt - b.createdAt);
  },

  /**
   * 最近 N 条消息。走复合索引倒序游标，只读需要的部分，
   * 不做「读全表再 slice」—— 长会话下差别很大。
   */
  async latest(sessionId: string, limit = 30): Promise<Message[]> {
    const desc = await dbQueryByIndexDesc<Message>(
      STORE.messages,
      'sessionCreated',
      limit,
      IDBKeyRange.bound([sessionId, -Infinity], [sessionId, Infinity]),
    );
    return desc.reverse();
  },

  /** 追加一条消息，并同步更新会话的预览与时间 */
  async append(input: SendMessageInput): Promise<Message> {
    const text = input.text;
    const createdAt = input.createdAt ?? Date.now();

    const message: Message = {
      id: uid(),
      sessionId: input.sessionId,
      sender: input.sender,
      type: input.type ?? 'text',
      text,
      fileId: input.fileId,
      createdAt,
      status: 'sent',
    };

    await dbPut(STORE.messages, message);

    // 会话预览：文本取原文（超长截断），附件类消息显示类型名
    const preview =
      message.type === 'text'
        ? text.slice(0, 60)
        : `[${TYPE_LABEL[message.type] ?? message.type}]`;
    await sessionsRepo.touch(input.sessionId, preview, createdAt);

    eventBus.emit('message:appended', {
      sessionId: input.sessionId,
      messageId: message.id,
    });
    return message;
  },

  async updateStatus(id: string, status: MessageStatus): Promise<void> {
    const m = await dbGet<Message>(STORE.messages, id);
    if (!m) return;
    m.status = status;
    await dbPut(STORE.messages, m);
    eventBus.emit('message:list-changed', { sessionId: m.sessionId });
  },

  /** 全局搜索消息文本 */
  async search(keyword: string): Promise<Message[]> {
    const all = await dbQueryByIndex<Message>(STORE.messages, 'sessionId', IDBKeyRange.lowerBound(''));
    const kw = keyword.trim().toLowerCase();
    if (!kw) return [];
    return all
      .filter((m) => m.type === 'text' && m.text.toLowerCase().includes(kw))
      .sort((a, b) => b.createdAt - a.createdAt);
  },

  /** 某会话内搜索 */
  async searchInSession(sessionId: string, keyword: string): Promise<Message[]> {
    const list = await this.listBySession(sessionId);
    const kw = keyword.trim().toLowerCase();
    if (!kw) return [];
    return list.filter((m) => m.text.toLowerCase().includes(kw));
  },

  async remove(id: string): Promise<void> {
    const m = await dbGet<Message>(STORE.messages, id);
    if (!m) return;
    await dbDelete(STORE.messages, id);
    if (m.fileId) await dbDelete(STORE.files, m.fileId);
    eventBus.emit('message:list-changed', { sessionId: m.sessionId });
  },

  /** 全部标记已读 */
  async markAllRead(): Promise<number> {
    const all = await dbQueryByIndex<Message>(STORE.messages, 'sessionId', IDBKeyRange.lowerBound(''));
    const dirty = all.filter((m) => m.status !== 'read');
    if (dirty.length === 0) return 0;
    await Promise.all(dirty.map((m) => dbPut(STORE.messages, { ...m, status: 'read' as const })));
    return dirty.length;
  },
};

const TYPE_LABEL: Record<string, string> = {
  image: '图片',
  file: '文件',
  voice: '语音',
};
